import type { FC } from 'react';

import { useCms, useContent } from '@eneco/cms';
import { TileBarWrapper, TopTaskBadge } from '@eneco/custom-components/TopTaskTileBar';
import { Box, Hidden, Stack, Text, TileBar } from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';
import { ChevronRightIcon } from '@eneco/sparky/icons';
import type { LinkFieldValue } from '@sitecore-jss/sitecore-jss-react';

import { getIllustration } from './illustrations';
import type { TopTasksContent } from './top-tasks-content.types';

/**
 * `TopTasks` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy component (`libs/sitecore/components/content/src/TopTasks/TopTasks.tsx`): a
 * `TileBar` with one tile per item, an illustration, title, optional badge and hover subtitle, laid out
 * as a row on desktop and a stacked list on mobile.
 *
 * See `top-tasks-content.types.ts` — the migrated Sanity schema carries no `items` field at all, so this
 * only ever renders on the Sitecore lane today. `TopTaskCTA` is a separate, independently-existing
 * legacy component (not a split-off part of this one) also allowed in the same `Hero` slot — see its
 * own doc comment.
 */
const TopTasks: FC = () => {
  const { adapter } = useCms();
  const content = useContent<TopTasksContent>();
  const isMobile = !useMediaQuery('md');

  const items = content.items ?? [];
  if (items.length === 0) return null;

  return (
    <Box paddingTop="8">
      <TileBarWrapper>
        <TileBar direction={{ initial: 'column', md: 'row' }}>
          {items.map((item, index) => {
            const link = adapter.toLink(item.link);
            if (!link?.href) return null;

            const linkValue: LinkFieldValue = { href: link.href, linktype: link.linkType ?? 'internal', target: link.target ?? '', querystring: '', anchor: '' };
            const Illustration = getIllustration(item.illustration);
            const ariaLabel = [item.title, item.subTitle].filter(Boolean).join(' ');

            return (
              <TileBar.Item
                linkValue={linkValue}
                linkType={linkValue.linktype}
                key={item._key ?? String(index)}
                ariaLabel={ariaLabel}
                hoverContent={
                  item.subTitle ? (
                    <Text size="BodyXS" align="center">
                      {item.subTitle}
                    </Text>
                  ) : undefined
                }>
                <Stack direction="row" alignY="center" alignX={{ initial: 'justify', md: 'center' }}>
                  <Stack direction={{ initial: 'row', md: 'column' }} gap={{ initial: '6', md: '3' }} alignY="center" alignX={{ initial: 'start', md: 'center' }}>
                    {!isMobile && item.badgeText ? <TopTaskBadge color="highemphasis">{item.badgeText}</TopTaskBadge> : null}
                    {Illustration && <Illustration size={{ initial: 'small', md: 'medium' }} color="iconSecondary" />}
                    <Text align={{ initial: 'left', md: 'center' }} size="BodyS" weight="bold">
                      {item.title}
                    </Text>
                  </Stack>
                  <Hidden above="md">
                    <Stack direction="row" gap="3" alignX="end">
                      {isMobile && item.badgeText ? <TopTaskBadge color="highemphasis">{item.badgeText}</TopTaskBadge> : null}
                      <div>
                        <ChevronRightIcon />
                      </div>
                    </Stack>
                  </Hidden>
                </Stack>
              </TileBar.Item>
            );
          })}
        </TileBar>
      </TileBarWrapper>
    </Box>
  );
};

export default TopTasks;
